import type { ActivityPeriod } from '@vlastiq/contracts';

/**
 * Naming the people in the activity card, and rounding their shares.
 *
 * ⚠️ PURE, AND IN `lib/` FOR THE USUAL REASON. Every tested module in this
 * frontend pulls in neither React nor HeroUI — the runner is Bun with no DOM —
 * and the two decisions below are exactly the kind that are wrong in a way you
 * only notice six months later, in production, with real names in the list.
 */

/**
 * The switcher, in the order it is rendered.
 *
 * ⚠️ «Всё», NOT «Всё время», AND THAT IS A MEASUREMENT. At 320px the four
 * buttons came to 16px more than the card could hold and «Неделя» was clipped
 * against the right edge — a control, unlike a table, has no scroll affordance,
 * so the last option was simply half-missing. «Всё время» was 96px of the 256
 * available; «Всё» is ~50 and the row fits from 320 up.
 *
 * The word it drops is carried by the footnote under the bars, which names the
 * first day the log actually covers — a more honest statement of what "all"
 * means here than the word «время» ever was.
 */
export const PERIOD_LABELS: Record<ActivityPeriod, string> = {
  all: 'Всё',
  year: 'Год',
  month: 'Месяц',
  week: 'Неделя',
};

/*
  ⚠️ `nameActivityShares` LIVES IN THE CONTRACT PACKAGE, not here, and is
  re-exported so this module stays the one place the card imports from. The
  end-of-month announcement names the same people from the same data — if the
  two disambiguated duplicate first names differently, the message in the group
  would credit «Лёха» while the card beside it showed two of them.
*/
export { nameActivityShares } from '@vlastiq/contracts';

/**
 * A share as a whole percent.
 *
 * ⚠️ THE COLUMN DOES NOT SUM TO 100, AND FORCING IT TO WOULD BE WORSE. Rounding
 * six shares independently can total 99 or 101; the usual fix — give the
 * remainder to the largest — makes one person's number depend on everybody
 * else's rounding error, so the same person's share changes when an unrelated
 * row appears. A column that is honest about each row is the better trade, and
 * nothing here presents the total as a sum.
 *
 * ⚠️ A NON-ZERO SHARE NEVER ROUNDS TO «0 %». Somebody who spoke is not absent,
 * and 0 % beside a visible bar reads as a rendering fault. Below half a percent
 * it becomes «<1 %».
 */
export function formatShare(share: number): string {
  if (share > 0 && share < 0.005) return '<1 %';
  return `${Math.round(share * 100)} %`;
}
